import React, { useState, useEffect } from "react";
import ReactPaginate from "react-paginate";
import { useDispatch, useSelector } from "react-redux";
import { getOrders, AssignJobsToDriver } from "../../store/actions/user-action";
import Table from "../../components/Table/Table";
import Loader from  "../../components/loader"
import { Link } from "react-router-dom";
import InputComponent from "../../components/Input";
import { MdBorderColor } from "react-icons/md";
import networkInstance from "../../services/api";
import toastMessage from "../../toast";

const ViewAllOrders = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(() => {
    return localStorage.getItem('selectedOrderId') || null;
  });
  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem('viewAllOrdersPage');
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const [limit] = useState(20);
  const [cancelConfirm, setCancelConfirm] = useState({ show: false, orderId: null, order: null });
  const [cancelling, setCancelling] = useState(false);
  const [assignConfirm, setAssignConfirm] = useState({ show: false, orderId: null, order: null });
  const [assigning, setAssigning] = useState(false);

  const { orders } = useSelector((state) => state.orderDetails);
  const totalPages = orders?.totalPages || 1;

  useEffect(() => {
    const timer = setTimeout(() => {
      const isNumeric = /^\d+$/.test(searchValue);
      const params = isNumeric 
        ? { mobileNumber: searchValue, page, limit }
        : { fullName: searchValue, page, limit };
      dispatch(getOrders(params));
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchValue, page, limit, dispatch]);

  const handlePageChange = (selectedItem) => {
    const newPage = selectedItem.selected + 1;
    setPage(newPage);
    localStorage.setItem('viewAllOrdersPage', newPage.toString());
  };
  const handleChange = (name, value) => {
    setSearchValue(value);
  };

  const handleAssignDriver = async () => {
    setAssigning(true);
    try {
      await dispatch(AssignJobsToDriver({ orderIds: [assignConfirm.orderId] }));
    } finally {
      setAssigning(false);
      setAssignConfirm({ show: false, orderId: null, order: null });
    }
  };

  const handleCancelOrder = async () => {
    setCancelling(true);
    try {
      const { data } = await networkInstance.patch(`orders/cancel_order?order_id=${cancelConfirm.orderId}`);
      if (data?.code === 200) {
        toastMessage("success", "Order cancelled successfully");
        const isNumeric = /^\d+$/.test(searchValue);
        const params = isNumeric
          ? { mobileNumber: searchValue, page, limit }
          : { fullName: searchValue, page, limit };
        dispatch(getOrders(params));
      } else {
        toastMessage("error", data?.message || "Failed to cancel order");
      }
    } catch (error) {
      toastMessage("error", "Failed to cancel order");
    } finally {
      setCancelling(false);
      setCancelConfirm({ show: false, orderId: null, order: null });
    }
  };

  return (
    <div>
      <div className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-gray-900">
            View All Orders
          </h2>
          <div className="ml-auto w-1/5">
            <InputComponent
              icon={<MdBorderColor />}
              value={searchValue}
              type="text"
              name="input"
              placeholder="Enter Mobile Number or Name"
              onChange={(e) => handleChange("input", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="p-6 rounded-md border bg-white">
        {orders?.result?.length > 0 ? (
          <>
            <div className="overflow-x-auto w-full">
              <Table
                header={[
                  "Customer",
                  "Phone",
                  "Source",
                  "Order Status",
                  "Items",
                  "Pickup Date",
                  "Pickup Slot",
                  "Order Date",
                  "Payment Status",
                  "Action",
                ]}
              >
                {orders.result.map((item, index) => (
                  <tr
                    key={index}
                    className={`${
                      selectedOrderId === item?.order_id
                        ? "bg-blue-100 border-blue-300"
                        : index % 2 === 0
                        ? "bg-gray-50"
                        : "bg-white"
                    }`}
                  >
                    <td className="py-1.5 px-3 text-xs text-gray-800 border-t whitespace-nowrap max-w-[9rem] truncate">
                      {item?.user?.full_name}
                    </td>
                    <td className="py-1.5 px-3 text-xs text-gray-800 border-t whitespace-nowrap">
                      {item?.user?.phone}
                    </td>
                    <td className="py-1.5 px-3 text-xs text-gray-800 border-t whitespace-nowrap capitalize">
                      {item?.user?.location} / {item?.source ?? 'app'}
                    </td>
                    <td className="py-1.5 px-3 text-xs border-t whitespace-nowrap">
                      {item?.status ? (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize
                          ${ item.status === 'Delivered' ? 'bg-green-100 text-green-700'
                            : item.status === 'Order Cancelled' ? 'bg-red-100 text-red-700'
                            : item.status === 'Confirmed' ? 'bg-blue-100 text-blue-700'
                            : !['Delivered', 'Order Cancelled', 'Confirmed'].includes(item.status) ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-600'}`}
                        >
                          {item?.status}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-1.5 px-3 text-xs text-gray-800 border-t whitespace-nowrap text-center">
                      {item.order_items?.length}
                    </td>
                    <td className="py-1.5 px-3 text-xs text-gray-800 border-t whitespace-nowrap">
                      {item.pickup_date &&
                        new Date(item.pickup_date).toLocaleDateString("en-GB", {
                          day: "2-digit", month: "short", year: "2-digit",
                        })}
                    </td>
                    <td className="py-1.5 px-3 text-xs text-gray-800 border-t whitespace-nowrap capitalize">
                      {item.pickup_time}
                    </td>
                    <td className="py-1.5 px-3 text-xs text-gray-800 border-t whitespace-nowrap">
                      {item.createdAt &&
                        new Date(item.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit", month: "short", year: "2-digit",
                        })}
                    </td>
                    <td className="py-1.5 px-3 text-xs text-gray-800 border-t whitespace-nowrap capitalize">
                      {item.payment_status}
                    </td>
                    <td className="py-1.5 px-3 text-xs text-gray-800 border-t whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Link
                          className="text-white border border-primaryRed bg-primaryRed hover:bg-hoverRed font-medium rounded text-xs px-2 py-0.5 whitespace-nowrap"
                          to={`/track-order?order_id=${item?.order_id}`}
                          onClick={() => {
                            setSelectedOrderId(item?.order_id);
                            localStorage.setItem('selectedOrderId', item?.order_id);
                          }}
                        >
                          View
                        </Link>
                        {item?.status?.toLowerCase() === 'confirmed' && (
                          <button
                            className="text-orange-500 border border-orange-500 bg-transparent hover:bg-orange-500 hover:text-white font-medium rounded text-xs px-2 py-0.5 whitespace-nowrap"
                            onClick={() => setCancelConfirm({ show: true, orderId: item?.order_id, order: item })}
                          >
                            Cancel
                          </button>
                        )}
                        {(['Confirmed', 'Ready to Deliver'].includes(item?.status)) && (
                          <button
                            className="text-blue-500 border border-blue-500 bg-transparent hover:bg-blue-500 hover:text-white font-medium rounded text-xs px-2 py-0.5 whitespace-nowrap"
                            onClick={() => setAssignConfirm({ show: true, orderId: item?.order_id, order: item })}
                          >
                            Assign
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </Table>
            </div>

            {/* Pagination Component */}
            <div className="flex justify-center items-center mt-4">
              <ReactPaginate
                breakLabel="..."
                nextLabel="Next"
                onPageChange={handlePageChange}
                pageRangeDisplayed={3}
                marginPagesDisplayed={2}
                pageCount={totalPages}
                previousLabel="Previous"
                containerClassName="flex space-x-2"
                pageClassName="px-3 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-orange-400 hover:text-white"
                activeClassName="bg-orange-500 text-white"
                previousClassName="px-4 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-orange-400 hover:text-white disabled:opacity-50"
                nextClassName="px-4 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-orange-400 hover:text-white disabled:opacity-50"
                forcePage={page - 1}
              />
            </div>
          </>
        ) : (
         <Loader/>
        )}
      </div>

      {cancelConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Order</h3>
            <div className="bg-gray-50 rounded-lg p-4 mb-5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Customer</span>
                <span className="font-medium text-gray-800">{cancelConfirm.order?.user?.full_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">City</span>
                <span className="font-medium text-gray-800 capitalize">{cancelConfirm.order?.user?.location}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Address</span>
                <span className="font-medium text-gray-800 text-right max-w-[60%]">{cancelConfirm.order?.address}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Pickup Date</span>
                <span className="font-medium text-gray-800">
                  {cancelConfirm.order?.pickup_date && new Date(cancelConfirm.order.pickup_date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">No. of Pairs</span>
                <span className="font-medium text-gray-800">{cancelConfirm.order?.order_items?.length}</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">Are you sure you want to cancel order <span className="font-medium">{cancelConfirm.orderId}</span>?</p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                onClick={() => setCancelConfirm({ show: false, orderId: null, order: null })}
                disabled={cancelling}
              >
                No
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                onClick={handleCancelOrder}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
      {assignConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assign Driver</h3>
            <div className="bg-gray-50 rounded-lg p-4 mb-5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Customer</span>
                <span className="font-medium text-gray-800">{assignConfirm.order?.user?.full_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">City</span>
                <span className="font-medium text-gray-800 capitalize">{assignConfirm.order?.user?.location}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Address</span>
                <span className="font-medium text-gray-800 text-right max-w-[60%]">{assignConfirm.order?.address}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Pickup Date</span>
                <span className="font-medium text-gray-800">
                  {assignConfirm.order?.pickup_date && new Date(assignConfirm.order.pickup_date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">No. of Pairs</span>
                <span className="font-medium text-gray-800">{assignConfirm.order?.order_items?.length}</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">Are you sure you want to assign a driver for order <span className="font-medium">{assignConfirm.orderId}</span>?</p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                onClick={() => setAssignConfirm({ show: false, orderId: null, order: null })}
                disabled={assigning}
              >
                No
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                onClick={handleAssignDriver}
                disabled={assigning}
              >
                {assigning ? 'Assigning...' : 'Yes, Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewAllOrders;
